import { Box, CircularProgress } from '@mui/material'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import { ChatInterface } from './components/ChatInterface'
import { ErrorBoundary } from './components/ErrorBoundary'
import { LoginPage } from './components/LoginPage'
import { AuthProvider, useAuth } from './contexts/AuthContext'

const GOOGLE_CLIENT_ID = '747365686519-t9cpu8cu31sv6jgru2n3r1llrfrjgcj6.apps.googleusercontent.com'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    )
  }

  // If not authenticated, redirect to login with return URL
  if (!isAuthenticated) {
    const currentLocation = window.location.pathname + window.location.search
    return <Navigate to={`/login?returnTo=${encodeURIComponent(currentLocation)}`} replace />
  }

  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    )
  }

  if (isAuthenticated) {
    const urlParams = new URLSearchParams(window.location.search)
    const returnTo = urlParams.get('returnTo')
    // Don't redirect to login page - always go to chat instead
    const redirectTo = returnTo && returnTo !== '/login' ? decodeURIComponent(returnTo) : '/chat'
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <Router>
            <Routes>
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <ErrorBoundary>
                      <LoginPage />
                    </ErrorBoundary>
                  </PublicRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ChatInterface />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/chat" replace />} />
            </Routes>
          </Router>
        </AuthProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  )
}

export default App
